// Builds media-host delivery URLs directly so the component renders a complete
// <video src/poster> during server rendering (no browser-only SDK).
//
// Delivery choices for web video:
// - `.mp4` extension: every uploaded source format (webm, mov, …) is
//   transcoded to progressive MP4, which plays in every browser.
// - `vc_auto,q_auto`: the host picks the best codec and quality for the
//   requested container.
// - `c_limit,w_1920`: scales oversized originals down to a web-appropriate
//   width but never upscales.

// A transformation component as an object, e.g.
// { c: 'fill', ar: '16:9' } serializes to "c_fill,ar_16:9"
export type VideoTransformations = { [key: string]: string | number };

const defaultVideoTransformations: VideoTransformations = {
  q: 'auto',
  vc: 'auto',
  c: 'limit',
  w: 1920,
};

// Poster is a single still frame: take the first frame (so_0), same
// downscale cap as the video itself. Format comes from the .jpg extension.
const defaultPosterTransformations: VideoTransformations = {
  so: 0,
  q: 'auto',
  c: 'limit',
  w: 1920,
};

// Custom posters are uploaded images, so they get the image pipeline's
// automatic format/quality.
const defaultCustomPosterTransformations: VideoTransformations = {
  f: 'auto',
  q: 'auto',
  c: 'limit',
  w: 1920,
};

function serializeTransformations(transformations: VideoTransformations): string {
  return Object.entries(transformations)
    .map(([key, value]) => (value === '' ? key : `${key}_${value}`))
    .join(',');
}

// Accepts the raw string form ("c_fill,ar_16:9") and parses it into an object
// so it merges over the defaults the same way object input does.
function parseTransformationString(value: string): VideoTransformations {
  const result: VideoTransformations = {};
  value.split(',').forEach((entry) => {
    const trimmed = entry.trim();
    if (!trimmed) {
      return;
    }
    const separatorIndex = trimmed.indexOf('_');
    if (separatorIndex <= 0) {
      // Flag-style component with no value (e.g. "so_0" handled above; keep
      // unknown flags as bare keys so they serialize unchanged).
      result[trimmed] = '';
      return;
    }
    result[trimmed.slice(0, separatorIndex)] = trimmed.slice(separatorIndex + 1);
  });
  return result;
}

function mergeTransformations(
  defaults: VideoTransformations,
  transformations?: VideoTransformations | string
): VideoTransformations {
  const userTransformations =
    typeof transformations === 'string'
      ? parseTransformationString(transformations)
      : transformations;
  return { ...defaults, ...userTransformations };
}

export function buildVideoSrc(
  cloudName: string,
  publicId: string,
  transformations?: VideoTransformations | string
): string {
  const merged = mergeTransformations(defaultVideoTransformations, transformations);
  // encodeURI preserves the slashes that are part of public IDs
  // (e.g. "videos/<uuid>/<id>") while escaping unsafe characters
  return encodeURI(
    `https://res.cloudinary.com/${cloudName}/video/upload/${serializeTransformations(merged)}/${publicId}.mp4`
  );
}

// First frame of the video itself — the zero-configuration poster.
export function buildVideoPosterSrc(cloudName: string, publicId: string): string {
  return encodeURI(
    `https://res.cloudinary.com/${cloudName}/video/upload/${serializeTransformations(defaultPosterTransformations)}/${publicId}.jpg`
  );
}

// A separately uploaded image chosen as the poster.
export function buildCustomPosterSrc(cloudName: string, posterPublicId: string): string {
  return encodeURI(
    `https://res.cloudinary.com/${cloudName}/image/upload/${serializeTransformations(defaultCustomPosterTransformations)}/${posterPublicId}`
  );
}
